/**
 * 문제집 배정 API 서비스
 * - 학생에게 문제집 배정
 * - 배정 조회, 수정, 삭제
 */

import axios from '../config/axios';
import type {
  Assignment,
  AssignWorkbookRequest,
  AssignWorkbookResponse,
  StudentAssignment,
  AssignmentStats,
  UpdateAssignmentRequest,
} from '../types/assignment';

/**
 * 배정 서비스 클래스
 */
class AssignmentService {
  /**
   * 학생에게 문제집 배정
   * @param workbookName - 문제집명
   * @param request - 배정 요청 (student_ids, due_date)
   * @returns 배정 결과
   */
  async assignWorkbook(
    workbookName: string,
    request: AssignWorkbookRequest
  ): Promise<AssignWorkbookResponse> {
    const response = await axios.post<AssignWorkbookResponse>(
      `/assignments/${encodeURIComponent(workbookName)}/assign`,
      request
    );
    return response.data;
  }

  /**
   * 특정 학생의 배정된 문제집 목록 조회
   * @param studentId - 학생 ID
   * @param isCompleted - 완료 여부 필터 (optional)
   * @returns 배정 목록
   */
  async getStudentAssignments(
    studentId: string,
    isCompleted?: boolean
  ): Promise<Assignment[]> {
    const response = await axios.get<{ success: boolean; assignments: Assignment[] }>(
      `/assignments/students/${studentId}`,
      {
        params: { is_completed: isCompleted },
      }
    );
    return response.data.assignments;
  }

  /**
   * 특정 문제집의 배정된 학생 목록 조회
   * @param workbookName - 문제집명
   * @returns 배정 목록 (학생 정보 포함)
   */
  async getWorkbookAssignments(workbookName: string): Promise<StudentAssignment[]> {
    const response = await axios.get<{
      success: boolean;
      students: StudentAssignment[];
    }>(`/assignments/${encodeURIComponent(workbookName)}/students`);
    return response.data.students;
  }

  /**
   * 배정 정보 수정 (제출 기한, 완료 여부)
   * @param workbookName - 문제집명
   * @param studentId - 학생 ID
   * @param request - 수정 요청
   */
  async updateAssignment(
    workbookName: string,
    studentId: string,
    request: UpdateAssignmentRequest
  ): Promise<Assignment> {
    const response = await axios.put<{ success: boolean; assignment: Assignment }>(
      `/assignments/${encodeURIComponent(workbookName)}/students/${studentId}`,
      request
    );
    return response.data.assignment;
  }

  /**
   * 배정 취소 (삭제)
   * @param workbookName - 문제집명
   * @param studentId - 학생 ID
   */
  async deleteAssignment(workbookName: string, studentId: string): Promise<void> {
    await axios.delete(
      `/assignments/${encodeURIComponent(workbookName)}/students/${studentId}`
    );
  }

  /**
   * 문제집별 배정 통계 조회
   * (향후 백엔드 API 추가 시 구현)
   * @param workbookName - 문제집명
   * @returns 배정 통계
   */
  async getAssignmentStats(workbookName: string): Promise<AssignmentStats> {
    // TODO: 백엔드에 /assignments/{workbook_name}/stats 엔드포인트 추가 필요
    // 임시로 getWorkbookAssignments로 계산
    const assignments = await this.getWorkbookAssignments(workbookName);

    const assignedCount = assignments.length;
    const completedCount = assignments.filter((a) => a.is_completed).length;
    const inProgressCount = assignedCount - completedCount;
    const completionRate = assignedCount > 0 ? (completedCount / assignedCount) * 100 : 0;

    return {
      workbook_name: workbookName,
      total_students: 0, // 전체 학생 수는 별도 API 필요
      assigned_count: assignedCount,
      completed_count: completedCount,
      in_progress_count: inProgressCount,
      completion_rate: Math.round(completionRate * 10) / 10, // 소수점 1자리
    };
  }
}

export const assignmentService = new AssignmentService();
